import React from 'react'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import ListSubheader from '@mui/material/ListSubheader'
import { VariableSizeList } from 'react-window'

// Virtualization stuff from --> https://material-ui.com/components/autocomplete/

const LISTBOX_PADDING = 8 // px

/**
 * Renderiza una fila individual dentro de la lista virtualizada con soporte para texto truncado.
 * 
 * @param {object} props - Las props proporcionadas por `VariableSizeList`.
 * @param {React.ReactElement[]} props.data - El array de elementos de React a renderizar.
 * @param {number} props.index - El índice de la fila actual.
 * @param {object} props.style - El objeto de estilo calculado por `react-window` para posicionar la fila.
 * @returns {React.ReactElement} El elemento clonado con el estilo ajustado y soporte para ellipsis.
 */
function renderRow(props) {
  const { data, index, style } = props
  return React.cloneElement(data[index], {
    title: data[index].props.children,
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: 'inline',
    },
  })
}

const OuterElementContext = React.createContext({})

/**
 * Componente que envuelve el contenedor exterior de la lista virtualizada.
 * Utiliza el contexto para pasar props adicionales al elemento `div` exterior.
 */
const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext)
  return <div ref={ref} {...props} {...outerProps} />
})

/**
 * Hook personalizado que resetea la caché de `VariableSizeList` cuando los datos cambian.
 * 
 * @param {any} data - Los datos que, al cambiar, provocarán el reseteo de la caché.
 * @returns {React.RefObject} Una referencia al componente `VariableSizeList`.
 */
function useResetCache(data) {
  const ref = React.useRef(null)
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true)
    }
  }, [data])
  return ref
}

/**
 * @typedef {object} ListboxComponentProps
 * @property {React.ReactNode} children - Los elementos hijos a renderizar dentro de la lista virtualizada.
 */

/**
 * Un componente de listbox virtualizado optimizado para renderizar grandes listas de opciones
 * en componentes `FilterAutocomplete`.
 * 
 * Utiliza `react-window` para renderizar solo los elementos visibles en el viewport,
 * mejorando significativamente el rendimiento cuando se trabaja con listas extensas.
 * Incluye soporte para texto truncado con ellipsis y tooltips que muestran el texto completo
 * al pasar el ratón sobre opciones largas.
 * 
 * **Nota:** Este componente está diseñado para ser usado como la prop `ListboxComponent` de `Autocomplete`.
 * 
 * @component
 * @example
 * import { Autocomplete, TextField } from '@mui/material';
 * import { ListboxComponent } from './virtualized-list-box.component';
 * 
 * const MyFilterAutocomplete = () => {
 *   const options = Array.from({ length: 10000 }, (_, i) => ({
 *     key: i,
 *     value: `Opción muy larga número ${i + 1} que necesita ser truncada`
 *   }));
 * 
 *   return (
 *     <Autocomplete
 *       options={options}
 *       ListboxComponent={ListboxComponent}
 *       renderInput={(params) => <TextField {...params} label="Filtrar" />}
 *     />
 *   );
 * };
 * 
 * @param {ListboxComponentProps} props - Las props del componente.
 * @param {React.Ref} ref - Referencia forwarded al elemento raíz del listbox.
 */
export const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props
  const itemData = React.Children.toArray(children)
  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true })
  const itemCount = itemData.length
  const itemSize = smUp ? 36 : 48

  const getChildSize = (child) => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48
    }

    return itemSize
  }

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0)
  }

  const gridRef = useResetCache(itemCount)

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  )
})
